import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Sparkles, Check, X, Loader2, AtSign } from "lucide-react";
import api from "../lib/api";

export default function ProfileSetupPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState(user?.name || "");
  const [bio, setBio] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  // Auto-generate username from name
  useEffect(() => {
    if (user?.name && !username) {
      const gen = user.name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 15);
      setUsername(gen);
    }
  }, [user?.name, username]);

  // Check username availability
  useEffect(() => {
    if (!username || username.length < 3) { setUsernameAvailable(null); return; }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/profile/check-username/${username}`);
        setUsernameAvailable(r.data.available);
      } catch { setUsernameAvailable(null); }
      finally { setChecking(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !displayName.trim() || usernameAvailable !== true) return;
    setSaving(true);
    try {
      await api.post("/profile/setup", {
        username: username.toLowerCase(),
        display_name: displayName.trim(),
        bio: bio.trim(),
      });
      toast.success("Profile created! Welcome to App-Mi.ai");
      navigate("/feed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create profile");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="profile-setup-page">
      <div className="w-full max-w-lg animate-fade-in-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center shadow-lg">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-outfit text-2xl font-semibold text-foreground" data-testid="setup-heading">Complete Your Profile</h1>
          <p className="text-sm text-muted-foreground mt-1">Choose a username and tell the world about yourself</p>
        </div>

        <div className="bg-card rounded-2xl border border-border/60 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1.5">Username</label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
                <input
                  data-testid="setup-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20))}
                  placeholder="yourname"
                  className="w-full pl-9 pr-10 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checking && <Loader2 className="w-4 h-4 text-muted-foreground/70 animate-spin" />}
                  {!checking && usernameAvailable === true && <Check className="w-4 h-4 text-emerald-500" />}
                  {!checking && usernameAvailable === false && <X className="w-4 h-4 text-red-500" />}
                </div>
              </div>
              {usernameAvailable === true && <p className="text-[11px] text-emerald-400 mt-1">@{username} is available</p>}
              {usernameAvailable === false && <p className="text-[11px] text-red-500 mt-1">Username taken</p>}
              <p className="text-[11px] text-muted-foreground/70 mt-1">3-20 characters: letters, numbers, underscores</p>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1.5">Display Name</label>
              <input
                data-testid="setup-display-name-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your full name"
                maxLength={100}
                className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors"
              />
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1.5">Bio</label>
              <textarea
                data-testid="setup-bio-input"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself..."
                maxLength={300}
                rows={3}
                className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 resize-none transition-colors"
              />
              <p className="text-[11px] text-muted-foreground/70 mt-1 text-right">{bio.length}/300</p>
            </div>

            <Button
              data-testid="setup-submit-btn"
              type="submit"
              disabled={saving || !username || !displayName.trim() || usernameAvailable !== true}
              className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-400 hover:to-violet-600 text-white h-11 rounded-lg text-sm font-medium shadow-glow-sm hover:-translate-y-0.5 transition-all press-down disabled:opacity-40"
            >
              {saving ? "Creating..." : "Complete Setup"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
